import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

import type { GridApi, ColDef, ColGroupDef, ColumnState, CellClassParams, IRowNode, RowClassParams, SelectionColumnDef } from "ag-grid-community";
import type {
  GridReadyEvent, ModelUpdatedEvent, FilterChangedEvent, SelectionChangedEvent, ColumnVisibleEvent, StateUpdatedEvent, ColumnHeaderContextMenuEvent,
  CellMouseDownEvent, CellMouseOverEvent, DisplayedColumnsChangedEvent, SortChangedEvent
} from "ag-grid-community";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";

import { Menu, MenuItem, Tooltip, ListItemText, ListItemIcon, Pagination } from "@mui/material";
import { NestedMenuItem } from "mui-nested-menu";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";

import kutil from "@modules/kutil";
import { skydsGridTheme } from "@components/aggrid/aggrid_skyds_theme";

import { useCellRangeSelection, type CellRange } from "./aggrid_cell_range_selection";

export interface CustomColDef<TData = any, TValue = any> extends ColDef<TData, TValue> {
  summable?: boolean; // 합계 대상 여부
  summaryLabel?: string; // 합계행에 사용할 Label
}

export interface CustomColGroupDef<TData = any> extends ColGroupDef<TData> {
  children: (CustomColDef<TData> | CustomColGroupDef<TData>)[];
}

export type SummaryPosition = "top" | "bottom" | "none";

interface CustomGridProps extends AgGridReactProps {
  showInfo?: boolean;
  summaryPosition?: SummaryPosition;
  totalPage?: number;
  currPage?: number;
  onPageChange?: (page: number) => void;
}

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
}

interface State {
  isShowInfo: boolean;
  summaryPosition: SummaryPosition;
}

const defState: State = {
  isShowInfo: false,
  summaryPosition: "none",
};

function _CustomAgGridReact(
  {
    showInfo, summaryPosition,
    totalPage, currPage, onPageChange,
    defaultColDef, columnDefs, rowData, rowSelection, selectionColumnDef, getRowStyle,
    onGridReady, onModelUpdated, onFilterChanged, onSelectionChanged, onColumnVisible, onStateUpdated, onColumnHeaderContextMenu, onCellMouseDown, onCellMouseOver, onDisplayedColumnsChanged, onSortChanged,
    ...rest
  }: CustomGridProps
) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [rowCount, setRowCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [columnState, setColumnState] = useState<ColumnState[]>([]);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [pinnedTopRowData, setPinnedTopRowData] = useState<Record<string, any>[] | undefined>(undefined);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<Record<string, any>[] | undefined>(undefined);

  const {
    range,
    onCellMouseDown: onCellMouseDownForRangeSelection,
    onCellMouseOver: onCellMouseOverForRangeSelection,
    getCellRangePosition,
    clearRange,
    selectAll,
  } = useCellRangeSelection();

  const gridRef = useRef<AgGridReact>(null);

  const colIdsRef = useRef<string[]>([]);

  const isSelectionCol = useCallback((colId: string) => colId.startsWith("ag-Grid-"), []); // 선택/자동생성 컬럼
  const syncColIds = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const next = api.getAllDisplayedColumns()
      .map(c => c.getColId())
      .filter(id => !isSelectionCol(id)); // 선택 컬럼 제외

    const prev = colIdsRef.current;
    const isSame = prev.length === next.length && prev.every((v, i) => v === next[i]);
    if (isSame) return;

    colIdsRef.current = next;
    clearRange();
  }, [clearRange, isSelectionCol]);

  const calculateSummary = useCallback((api: GridApi) => {
    if (!api) {
      return;
    }
    if (state.summaryPosition === "none") {
      setPinnedTopRowData(undefined);
      setPinnedBottomRowData(undefined);
      return;
    }

    const flattenColDefs = (defs: (ColDef | ColGroupDef)[]): CustomColDef[] =>
      defs.flatMap((col) =>
      "children" in col ? flattenColDefs(col.children) : [col as CustomColDef],
    );
    const leafCols = flattenColDefs(columnDefs ?? []);

    const totals: Record<string, any> = {};

    leafCols.forEach((col) => {
      const c = col as CustomColDef;
      if (!c.field) {
        return;
      }
      if (c.summable) {
        totals[c.field] = 0;
      } else if (c.summaryLabel) {
        totals[c.field] = c.summaryLabel;
      }
    });

    // 필터링된 로우만 순회 (정렬 순서는 무관해도 되면 이걸로 충분)
    api.forEachNodeAfterFilter((node: any) => {
      if (!node.data) return;
      leafCols.forEach((col) => {
        const c = col as CustomColDef;
        if (c.summable && c.field) {
          totals[c.field] += Number(node.data[c.field as keyof any]) || 0;
        }
      });
    });

    if (state.summaryPosition === "top") {
      setPinnedTopRowData([totals]);
      setPinnedBottomRowData(undefined);
    } else {
      setPinnedTopRowData(undefined);
      setPinnedBottomRowData([totals]);
    }
  }, [columnDefs, state.summaryPosition]);

  const copySelectedRange = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api || !range) return false;

    const colIds = colIdsRef.current;
    const cStart = colIds.indexOf(range.startCol);
    const cEnd = colIds.indexOf(range.endCol);
    if (cStart < 0 || cEnd < 0) return false;

    const rMin = Math.min(range.startRow, range.endRow);
    const rMax = Math.max(range.startRow, range.endRow);
    const cMin = Math.min(cStart, cEnd);
    const cMax = Math.max(cStart, cEnd);

    // 탭/개행/따옴표 포함 값은 Excel 규칙대로 큰따옴표 이스케이프
    const escapeCell = (v: unknown): string => {
      const s = v == null ? "" : String(v);
      return /[\t\n\r"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines: string[] = [];
    for (let i = rMin; i <= rMax; i++) {
      const node = api.getDisplayedRowAtIndex(i);
      if (!node) continue;

      const cells: string[] = [];
      for (let c = cMin; c <= cMax; c++) {
        const value = api.getCellValue({ rowNode: node, colKey: colIds[c], useFormatter: true });
        cells.push(escapeCell(value));
      }
      lines.push(cells.join("\t"));
    }

    if (lines.length === 0) return false;
    return kutil.writeClipboard(lines.join("\r\n"));
  }, [range]);

  const customDefaultColDef = useMemo<CustomColDef>(() => {
    const getPos = (p: CellClassParams) => {
      if (p.node.rowPinned || p.node.rowIndex == null) return null;
      return getCellRangePosition(p.node.rowIndex, p.column.getColId(), colIdsRef.current);
    };

    return {
      ...defaultColDef,
      cellClassRules: {
        ...defaultColDef?.cellClassRules, // 사용자 정의 규칙 병합
        "range-cell":   (p: CellClassParams) => getPos(p) !== null,
        "range-top":    (p: CellClassParams) => !!getPos(p)?.top,
        "range-bottom": (p: CellClassParams) => !!getPos(p)?.bottom,
        "range-left":   (p: CellClassParams) => !!getPos(p)?.left,
        "range-right":  (p: CellClassParams) => !!getPos(p)?.right,
      },
    };
  }, [defaultColDef, getCellRangePosition]);

  const customRowSelection = useMemo(() => {
    if (typeof rowSelection === "object" && rowSelection !== null) {
      return { selectAll: "filtered" as const, ...rowSelection };
    }
    return rowSelection;
  }, [rowSelection]);

  const customSelectionColumnDef = useMemo(() => {
    if (typeof selectionColumnDef === "object" && selectionColumnDef !== null) {
      return { pinned: "left" as const, lockPinned: true, suppressHeaderMenuButton: true, ...selectionColumnDef };
    }
    return selectionColumnDef;
  }, [selectionColumnDef]);

  const customGetRowStyle = useCallback((params: RowClassParams) => {
    return {
      ...(params.node.rowPinned ? {"backgroundColor": "#e0f0ff"} : {}),
      ...(getRowStyle?.(params) ?? {})
    };
  }, [getRowStyle]);

  const customOnGridReady = useCallback((params: GridReadyEvent) => {
    setRowCount(params.api.getDisplayedRowCount());
    syncColIds();
    onGridReady?.(params);
  }, [onGridReady, syncColIds]);

  const customOnModelUpdated = useCallback((params: ModelUpdatedEvent) => {
    setRowCount(params.api.getDisplayedRowCount());
    onModelUpdated?.(params);
  }, [onModelUpdated]);

  const customOnFilterChanged = useCallback((params: FilterChangedEvent) => {
    setRowCount(params.api.getDisplayedRowCount());
    calculateSummary(params.api);
    clearRange();
    onFilterChanged?.(params);
  }, [onFilterChanged, calculateSummary, clearRange]);

  const customOnSelectionChanged = useCallback((params: SelectionChangedEvent) => {
    setSelectedCount(params.api.getSelectedRows().length);
    onSelectionChanged?.(params);
  }, [onSelectionChanged]);

  const customOnColumnVisible = useCallback((params: ColumnVisibleEvent) => {
    onColumnVisible?.(params);
  }, [onColumnVisible]);

  const customOnStateUpdated = useCallback((params: StateUpdatedEvent) => {
    const newColumnState = gridRef.current?.api.getColumnState() ?? [];

    setColumnState(prev => {
      const relevant = (s: ColumnState) => `${s.colId}:${s.hide}`;
      const isSame =
        prev.length === newColumnState.length &&
        prev.every((p, i) => relevant(p) === relevant(newColumnState[i]));

      if (isSame) return prev; // 불필요한 리렌더 방지

      const copied = kutil.deepCopy(newColumnState);
      copied.forEach(cs => {
        cs.headerName = gridRef.current?.api.getColumnDef(cs.colId)?.headerName;
      });
      return copied;
    });

    onStateUpdated?.(params);
  }, [onStateUpdated]);

  const customOnColumnHeaderContextMenu = useCallback((params: ColumnHeaderContextMenuEvent) => {
    onColumnHeaderContextMenu?.(params);
  }, [onColumnHeaderContextMenu]);

  const customOnCellMouseDown = useCallback((params: CellMouseDownEvent) => {
    if (!isSelectionCol(params.column.getColId())) {
      onCellMouseDownForRangeSelection(params); // 체크박스 컬럼에선 range 시작 안 함
    }
    onCellMouseDown?.(params);
  }, [onCellMouseDown, onCellMouseDownForRangeSelection]);

  const customOnCellMouseOver = useCallback((params: CellMouseOverEvent) => {
    if (!isSelectionCol(params.column.getColId())) {
      onCellMouseOverForRangeSelection(params); // 지나갈 때 endCol 오염 방지
    }
    onCellMouseOver?.(params);
  }, [onCellMouseOver, onCellMouseOverForRangeSelection]);

  const customOnDisplayedColumnsChanged = useCallback((params: DisplayedColumnsChangedEvent) => {
    syncColIds();
    onDisplayedColumnsChanged?.(params);
  }, [onDisplayedColumnsChanged, syncColIds]);

  const customOnSortChanged = useCallback((params: SortChangedEvent) => {
    clearRange();
    onSortChanged?.(params);
  }, [onSortChanged, clearRange]);

  useEffect(() => {
    setState(prev => ({ ...prev, isShowInfo: (showInfo ?? true) }));
  }, [showInfo]);

  useEffect(() => {
    const next = summaryPosition ?? (columnDefs?.some(col => (col as CustomColDef).summable) ? "top" : "none");
    setState(prev => ({ ...prev, summaryPosition: next }));
  }, [columnDefs, summaryPosition]);

  useEffect(() => {
    setIsMultiSelect(
      rowSelection === "multiple"
      || (typeof rowSelection === "object" && rowSelection?.mode === "multiRow")
    );
  }, [rowSelection]);

  useEffect(() => {
    if (gridRef.current) {
      calculateSummary(gridRef.current.api);
    }
  }, [rowData, calculateSummary]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    // api.setFilterModel(null); // 필터 초기화
    api.getColumns()?.filter((col) => col.isFilterActive()).forEach((col) => api.destroyFilter(col)); // 필터 초기화
    api.applyColumnState({ defaultState: { sort: null } }); // 정렬 초기화
    clearRange(); // 선택영역 초기화
  }, [rowData, clearRange, gridRef.current]);

  const prevRangeRef = useRef<CellRange | null>(null);
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const prev = prevRangeRef.current;
    prevRangeRef.current = range;

    // 해제: 정렬 등으로 행이 이동해 클래스가 흩어졌을 수 있으므로 전체 refresh
    if (!range) {
      if (prev) api.refreshCells({ force: true });
      return;
    }

    const targets = [range, prev].filter(Boolean) as CellRange[];
    const rMin = Math.min(...targets.flatMap(r => [r.startRow, r.endRow]));
    const rMax = Math.max(...targets.flatMap(r => [r.startRow, r.endRow]));
    const rowNodes: IRowNode[] = [];
    for (let i = Math.max(0, rMin); i <= rMax; i++) {
      const node = api.getDisplayedRowAtIndex(i);
      if (node) rowNodes.push(node);
    }
    if (rowNodes.length > 0) api.refreshCells({ rowNodes, force: true });
  }, [range]);

  function closeContextMenu() {
    setContextMenu(null);
  };

  function resetGrid() {
    gridRef.current?.api.resetColumnState();
    gridRef.current?.api.setFilterModel(null);
    clearRange();
    closeContextMenu();
  }

  function updateColumnVisible(colId: string, visible: boolean) {
    gridRef.current?.api.setColumnsVisible([colId], visible);
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const onContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const elementsStack = document.elementsFromPoint(e.clientX, e.clientY);
    const gridElement = elementsStack
      .map((el) => el.closest(".ag-root-wrapper"))
      .find((el) => el !== null) as HTMLElement | undefined;

    if (!gridElement) {
      closeContextMenu();
      return;
    }

    if (!wrapperRef.current?.contains(gridElement)) {
      closeContextMenu();

      const clientX = e.clientX;
      const clientY = e.clientY;
      setTimeout(() => {
        gridElement.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX,
            clientY,
            button: 2,
          })
        );
      }, 0);
      return;
    }

    setContextMenu({
      mouseX: e.clientX,
      mouseY: e.clientY,
    });
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey)) return;

    // 필터 입력창 등 내부 input은 방해하지 않음
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

    const key = e.key.toLowerCase();

    if (key === "c") {
      if (!range) return;
      e.preventDefault();
      copySelectedRange();
      return;
    }

    if (key === "a") {
      const api = gridRef.current?.api;
      const colIds = colIdsRef.current;
      if (!api || colIds.length === 0) return;

      const lastRowIndex = api.getDisplayedRowCount() - 1;
      if (lastRowIndex < 0) return; // 빈 그리드

      e.preventDefault(); // 브라우저 전체 페이지 선택 방지
      selectAll(lastRowIndex, colIds[0], colIds[colIds.length - 1]);
    }
  }, [range, copySelectedRange, selectAll]);

  return (
    <>
      <div ref={wrapperRef} onContextMenu={onContextMenu} onKeyDown={onKeyDown}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={customDefaultColDef}
          rowData={rowData}
          rowSelection={customRowSelection}
          selectionColumnDef={customSelectionColumnDef}
          getRowStyle={customGetRowStyle}
          pinnedTopRowData={pinnedTopRowData}
          pinnedBottomRowData={pinnedBottomRowData}
          theme={skydsGridTheme}
          onGridReady={customOnGridReady}
          onModelUpdated={customOnModelUpdated}
          onFilterChanged={customOnFilterChanged}
          onSelectionChanged={customOnSelectionChanged}
          onColumnVisible={customOnColumnVisible}
          onStateUpdated={customOnStateUpdated}
          onColumnHeaderContextMenu={customOnColumnHeaderContextMenu}
          onCellMouseDown={customOnCellMouseDown}
          onCellMouseOver={customOnCellMouseOver}
          onDisplayedColumnsChanged={customOnDisplayedColumnsChanged}
          onSortChanged={customOnSortChanged}
          {...rest}
        />
      </div>
      {/* 그리드 하단 */}
      {state.isShowInfo &&
      <div className="grid-bottom">
        <span>Total: { rowCount } { isMultiSelect && <em>Selected: { selectedCount }</em> }</span>
        {totalPage && currPage ? (
          <Pagination
            color="primary"
            count={totalPage}
            page={currPage}
            siblingCount={5}
            boundaryCount={0}
            showFirstButton
            showLastButton
            hidePrevButton
            hideNextButton
            size="small"
            onChange={(_event, page) => onPageChange?.(page)}
          />
        ) : null}
        <div>
          <Tooltip title="그리드 초기화">
            <button className="icon-button form-reset" onClick={() => resetGrid()}></button>
          </Tooltip>
        </div>
      </div>
      }
      {/* 컨텍스트 메뉴 */}
      <Menu
        open={contextMenu !== null}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        onClose={closeContextMenu}
        slotProps={{
          backdrop: {
            onContextMenu: onContextMenu,
          },
        }}
        sx={{
          "& .MuiMenuItem-root": {
            fontSize: "14px",
          },
        }}
      >
        <NestedMenuItem
          label="&nbsp;컬럼설정&nbsp;"
          parentMenuOpen={contextMenu !== null}
          MenuProps={{
            PaperProps: {
              sx: {
                minWidth: 200,
                maxHeight: 600,
                overflowY: "auto",
              },
            },
          }}
        >
          {columnState?.map((column) => {
            if (!column.headerName) return null;
            return (
              <MenuItem key={column.colId} onClick={(_e) => updateColumnVisible(column.colId!, !!column.hide)}>
                <ListItemIcon>
                  {(!column.hide) ? <CheckBox fontSize="small" color="primary" /> : <CheckBoxOutlineBlank fontSize="small" color="primary" />}
                </ListItemIcon>
                <ListItemText>{column.headerName}</ListItemText>
              </MenuItem>
            );
          })}
        </NestedMenuItem>
        <MenuItem onClick={() => resetGrid()}>초기화</MenuItem>
      </Menu>
    </>
  );
}

export const CustomAgGridReact = React.memo(_CustomAgGridReact);
